import { z } from 'zod';
import { uuidSchema } from './common.schema';

// ── Shared ──────────────────────────────────────────────────

export const LocaleMetaSchema = z.object({
  requestId: z.string(),
  timestamp: z.string(),
});

export const LocaleErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.object({ code: z.string(), message: z.string() }),
  meta: LocaleMetaSchema,
});

// ── GET /locale/map ─────────────────────────────────────────

export const LocaleMapResponseSchema = z.object({
  success: z.literal(true),
  data: z.record(z.string(), z.string()),
  meta: LocaleMetaSchema,
});

export type LocaleMap = z.infer<typeof LocaleMapResponseSchema>['data'];

// ── GET /locale/keys ────────────────────────────────────────

export const LocaleKeyTypeEnum = z.enum([
  'label', 'header', 'nav', 'msg',
  'field', 'button', 'tooltip', 'placeholder', 'error',
]);
export type LocaleKeyType = z.infer<typeof LocaleKeyTypeEnum>;

export const LocaleKeySchema = z.object({
  key: z.string(),
  base_value: z.string(),
  base_value_plural: z.string().nullable(),
  translated_value: z.string().nullable(),
  custom_value: z.string().nullable(),
  custom_value_plural: z.string().nullable(),
  resolved_value: z.string().nullable(),
  is_customized: z.boolean(),
  description: z.string().nullable(),
  module: z.string(),
  submodule: z.string().nullable(),
  key_type: LocaleKeyTypeEnum,
  has_plural: z.boolean(),
  is_active: z.boolean(),
});
export type LocaleKey = z.infer<typeof LocaleKeySchema>;

export const LocaleKeysQuerySchema = z.object({
  module: z.string().optional(),
  key_type: LocaleKeyTypeEnum.optional(),
  is_customized: z.enum(['true', 'false']).optional(),
  search: z.string().optional(),
  lang: z.string().optional(),
  page: z.string().regex(/^\d+$/).default('1').optional(),
  limit: z.string().regex(/^\d+$/).default('50').optional(),
});
export type LocaleKeysQuery = z.infer<typeof LocaleKeysQuerySchema>;

export const LocaleKeysResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(LocaleKeySchema),
  meta: LocaleMetaSchema.extend({
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
  }),
});

// ── PUT /locale/override/:tenantId/:lang ────────────────────

/** Query params for the batch override endpoint */
export const LocaleOverrideBatchQuerySchema = z.object({
  tenantId: uuidSchema,
  lang: z.string().min(2).describe('Language code — e.g. en, en-CA'),
});
export type LocaleOverrideBatchQuery = z.infer<typeof LocaleOverrideBatchQuerySchema>;

/** Single override item inside the batch body */
export const LocaleOverrideItemSchema = z.object({
  key: z.string().min(1).describe('Translation key (e.g. label.feed)'),
  custom_value: z.string().min(1).describe('Overridden translation value'),
  custom_value_plural: z.string().nullable().optional().describe('Plural form (if key has_plural = true)'),
});
export type LocaleOverrideItem = z.infer<typeof LocaleOverrideItemSchema>;

/** Batch body — array of 1–500 override items */
export const LocaleOverrideBodySchema = z.object({
  overrides: z.array(LocaleOverrideItemSchema).min(1).max(500)
    .describe('Array of override items (1–500)'),
});
export type LocaleOverrideBody = z.infer<typeof LocaleOverrideBodySchema>;

export const LocaleOverrideResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    processed: z.number(),
    total: z.number(),
    message: z.string(),
    bundle: z.record(z.string(), z.string()).describe('Full resolved locale bundle after override'),
  }),
  meta: LocaleMetaSchema,
});

// ── DELETE /locale/override/:key ────────────────────────────

export const LocaleOverrideParamsSchema = z.object({
  key: z.string().min(1).describe('Translation key to reset'),
});
export type LocaleOverrideParams = z.infer<typeof LocaleOverrideParamsSchema>;

// ── GET /locale/languages ───────────────────────────────────

export const LanguageSchema = z.object({
  language_code: z.string(),
  language_name: z.string(),
  native_name: z.string(),
  text_direction: z.string(),
  is_default: z.boolean(),
});
export type Language = z.infer<typeof LanguageSchema>;

export const LanguagesResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(LanguageSchema),
  meta: LocaleMetaSchema,
});

// ── POST /locale/import ─────────────────────────────────────

export const LocaleImportRowSchema = z.object({
  key: z.string().min(1),
  custom_value: z.string().min(1),
  custom_value_plural: z.string().nullable().optional(),
});
export type LocaleImportRow = z.infer<typeof LocaleImportRowSchema>;

export const LocaleImportBodySchema = z.object({
  rows: z.array(LocaleImportRowSchema).min(1).max(500),
});
export type LocaleImportBody = z.infer<typeof LocaleImportBodySchema>;

/** Query params required by POST /locale/import */
export const LocaleImportQuerySchema = z.object({
  tenant_id: uuidSchema,
  language_code: z.string().min(2).describe('Language code — e.g. en, en-CA'),
});
export type LocaleImportQuery = z.infer<typeof LocaleImportQuerySchema>;

export const LocaleImportResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    processed: z.number(),
    skipped: z.number().describe('Rows skipped because the key was not found in locale_strings'),
    total: z.number(),
    message: z.string(),
    bundle: z.record(z.string(), z.string()).describe('Full resolved locale bundle after import'),
  }),
  meta: LocaleMetaSchema,
});

// ── POST /locale/sync ────────────────────────────────────────────────────────

/**
 * Language codes that have a corresponding bundle in @tms-phoenix/tms-types.
 * Must stay in sync with the localeBundles export in packages/tms-types/src/locales/index.ts.
 */
export const SUPPORTED_SYNC_LANGUAGES = ['en', 'en-US', 'en-GB', 'de', 'fr'] as const;
export type SupportedSyncLanguage = (typeof SUPPORTED_SYNC_LANGUAGES)[number];

export const LocaleSyncBodySchema = z.object({
  tenant_id: uuidSchema,
  language_code: z
    .enum(SUPPORTED_SYNC_LANGUAGES)
    .describe('BCP-47 language code — must have a bundle in @tms-phoenix/tms-types'),
  is_initial_setup: z
    .boolean()
    .describe(
      'true = INSERT new locale_strings rows (skip existing). ' +
      'false = UPDATE translated_value on existing rows (skip unknown keys).',
    ),
});
export type LocaleSyncBody = z.infer<typeof LocaleSyncBodySchema>;

export const LocaleSyncResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    tenant_id: z.string(),
    language_code: z.string(),
    is_initial_setup: z.boolean(),
    s3_key: z.string().describe('S3 object key the bundle was written to'),
    db_rows_affected: z.number().describe('Number of rows inserted or updated in locale_strings'),
    total_keys: z.number().describe('Number of keys in the language bundle'),
  }),
  meta: LocaleMetaSchema,
});


export const LocaleBundleFileParamsSchema = z.object({
  subDomain: z.string().min(1)
});
export type LocaleBundleFileParams = z.infer<typeof LocaleBundleFileParamsSchema>;


//TO BE CHECKED AND CLEANED
/**
 * Locale / Translation Schemas
 *
 * Zod schemas for locale API request/response validation.
 * These schemas define the contract between frontend and backend
 * for the localization system.
 *
 * @see TMENS Localization Architecture — Hybrid DB + Mem-Cache
 */


// ── Language / Bundle schemas ───────────────────────────────────────────────

/** BCP-47 language code (e.g. 'en', 'en-GB', 'de-DE'). */
export const languageCodeSchema = z.string().min(2).max(10);

/** Flat key-value translation bundle: `{ "nav.dashboard": "Dashboard", ... }`. */
export const translationBundleSchema = z.record(z.string(), z.string());

/** Language metadata returned by GET /api/locale/languages. */
export const languageInfoSchema = z.object({
  code: languageCodeSchema,
  name: z.string(),
  nativeName: z.string(),
  direction: z.enum(['ltr', 'rtl']),
  isActive: z.boolean(),
});

/** Query params for GET /api/locale/bundle. */
export const fetchBundleQuerySchema = z.object({
  lang: languageCodeSchema,
  tenantId: uuidSchema.optional(),
  hospitalId: uuidSchema.optional(),
  scope: z.enum(['public', 'full']).optional(),
});

// ── Terminology Override schemas ────────────────────────────────────────────

/** Body for PUT /api/locale/override — upsert a terminology override. */
export const upsertTerminologyOverrideSchema = z.object({
  key: z.string().min(1).max(255),
  custom_value: z.string().min(1).max(2000),
  lang: languageCodeSchema,
  tenant_id: uuidSchema,
  hospital_id: uuidSchema.nullable().optional(),
});

/** Query params for DELETE /api/locale/override. */
export const deleteTerminologyOverrideQuerySchema = z.object({
  key: z.string().min(1).max(255),
  lang: languageCodeSchema,
  tenantId: uuidSchema,
  hospitalId: uuidSchema.optional(),
});

/** Terminology override record (as stored in DB / returned by admin API). */
export const terminologyOverrideSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  hospitalId: uuidSchema.nullable(),
  key: z.string(),
  customValue: z.string(),
  lang: languageCodeSchema,
  updatedBy: uuidSchema,
  updatedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});

/** Audit row for terminology override changes. */
export const terminologyOverrideAuditSchema = z.object({
  id: uuidSchema,
  overrideId: uuidSchema,
  previousValue: z.string().nullable(),
  newValue: z.string(),
  changedBy: uuidSchema,
  changedAt: z.string().datetime(),
  action: z.enum(['CREATE', 'UPDATE', 'DELETE']),
});

// ── Tenant locale configuration ─────────────────────────────────────────────

/** Tenant locale configuration (default language, enabled languages, etc.). */
export const tenantLocaleConfigSchema = z.object({
  tenantId: uuidSchema,
  defaultLanguage: languageCodeSchema,
  enabledLanguages: z.array(languageCodeSchema),
  fallbackLanguage: languageCodeSchema.optional(),
});

// ── Inferred Types ──────────────────────────────────────────────────────────

export type TranslationBundle = z.infer<typeof translationBundleSchema>;
export type LanguageInfo = z.infer<typeof languageInfoSchema>;
export type FetchBundleQuery = z.infer<typeof fetchBundleQuerySchema>;
export type UpsertTerminologyOverride = z.infer<typeof upsertTerminologyOverrideSchema>;
export type DeleteTerminologyOverrideQuery = z.infer<typeof deleteTerminologyOverrideQuerySchema>;
export type TerminologyOverride = z.infer<typeof terminologyOverrideSchema>;
export type TerminologyOverrideAudit = z.infer<typeof terminologyOverrideAuditSchema>;
export type TenantLocaleConfig = z.infer<typeof tenantLocaleConfigSchema>;
